import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import { formatDate } from '../../../utils/dateUtils';

const SubUnionCard = ({ 
  subUnion, 
  onEdit, 
  onView, 
  onToggleStatus, 
  onSelect, 
  isSelected = false,
  viewMode = 'grid' 
}) => {
  const handleStatusToggle = (e) => {
    e?.stopPropagation();
    onToggleStatus(subUnion?.id, !subUnion?.isActive);
  };

  const handleSelect = (e) => {
    e?.stopPropagation();
    onSelect(subUnion?.id);
  };

  const handleEdit = (e) => {
    e?.stopPropagation();
    onEdit(subUnion?.id);
  };

  const handleView = (e) => {
    e?.stopPropagation();
    onView(subUnion?.id);
  };

  if (viewMode === 'table') {
    return (
      <tr className={`hover:bg-muted/50 transition-colors ${isSelected ? 'bg-accent/10' : ''}`}>
        <td className="px-4 py-3">
          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              checked={isSelected}
              onChange={handleSelect}
              className="w-4 h-4 text-primary border-border rounded focus:ring-primary"
            />
            <div className="w-10 h-10 rounded-lg overflow-hidden bg-muted flex-shrink-0">
              <Image
                src={subUnion?.logo}
                alt={subUnion?.logoAlt}
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </td>
        <td className="px-4 py-3">
          <div>
            <h3 className="font-medium text-foreground">{subUnion?.name?.ar}</h3>
            <p className="text-sm text-muted-foreground">{subUnion?.name?.en}</p>
          </div>
        </td>
        <td className="px-4 py-3">
          <div className="flex items-center space-x-2">
            <Icon name="MapPin" size={16} className="text-muted-foreground" />
            <span className="text-sm text-foreground">{subUnion?.city?.name?.ar}</span>
          </div>
        </td>
        <td className="px-4 py-3">
          <div className="flex items-center space-x-2">
            <Icon name="Users" size={16} className="text-muted-foreground" />
            <span className="text-sm text-foreground">{subUnion?.memberCount}</span>
          </div>
        </td>
        <td className="px-4 py-3">
          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
            subUnion?.isActive 
              ? 'bg-success/10 text-success border border-success/20' :'bg-error/10 text-error border border-error/20'
          }`}>
            <div className={`w-2 h-2 rounded-full mr-2 ${
              subUnion?.isActive ? 'bg-success' : 'bg-error'
            }`} />
            {subUnion?.isActive ? 'نشط' : 'غير نشط'}
          </span>
        </td>
        <td className="px-4 py-3">
          <div className="flex items-center space-x-2">
            <Button
              variant="ghost"
              size="icon"
              className="w-8 h-8"
              onClick={handleView}
              iconName="Eye"
              iconSize={16}
              title="عرض التفاصيل"
            />
            <Button
              variant="ghost"
              size="icon"
              className="w-8 h-8"
              onClick={handleEdit}
              iconName="Edit"
              iconSize={16}
              title="تعديل"
            />
            <Button
              variant="ghost"
              size="icon"
              className={`w-8 h-8 ${subUnion?.isActive ? 'text-error hover:text-error' : 'text-success hover:text-success'}`}
              onClick={handleStatusToggle}
              iconName={subUnion?.isActive ? 'UserX' : 'UserCheck'}
              iconSize={16}
              title={subUnion?.isActive ? 'إلغاء التفعيل' : 'تفعيل'}
            />
          </div>
        </td>
      </tr>
    );
  }

  return (
    <div className={`bg-card border border-border rounded-lg p-4 hover:shadow-elevation-2 transition-all duration-200 ${
      isSelected ? 'ring-2 ring-primary/20 border-primary/30' : ''
    }`}>
      {/* Header with selection and status */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center space-x-3">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={handleSelect}
            className="w-4 h-4 text-primary border-border rounded focus:ring-primary"
          />
          <div className="w-12 h-12 rounded-lg overflow-hidden bg-muted flex-shrink-0">
            <Image
              src={subUnion?.logo}
              alt={subUnion?.logoAlt}
              className="w-full h-full object-cover"
            />
          </div>
        </div>
        
        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
          subUnion?.isActive 
            ? 'bg-success/10 text-success border border-success/20' :'bg-error/10 text-error border border-error/20'
        }`}>
          <div className={`w-2 h-2 rounded-full mr-2 ${
            subUnion?.isActive ? 'bg-success' : 'bg-error'
          }`} />
          {subUnion?.isActive ? 'نشط' : 'غير نشط'}
        </span>
      </div>
      {/* Content */}
      <div className="space-y-3">
        <div>
          <h3 className="font-semibold text-foreground text-lg font-arabic">{subUnion?.name?.ar}</h3>
          <p className="text-sm text-muted-foreground">{subUnion?.name?.en}</p>
        </div>

        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center space-x-2">
            <Icon name="MapPin" size={16} className="text-muted-foreground" />
            <span className="text-foreground">{subUnion?.city?.name?.ar}</span>
          </div>
          
          <div className="flex items-center space-x-2">
            <Icon name="Users" size={16} className="text-muted-foreground" />
            <span className="text-foreground font-medium">{subUnion?.memberCount}</span>
          </div>
        </div>

        {subUnion?.description && (
          <p className="text-sm text-muted-foreground line-clamp-2">
            {subUnion?.description?.ar}
          </p>
        )}

        <div className="text-xs text-muted-foreground">
          <p>تاريخ التأسيس: {formatDate(subUnion.establishedDate)}</p>
        </div>
      </div>
      {/* Actions */}
      <div className="flex items-center justify-between mt-4 pt-3 border-t border-border">
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleView}
            iconName="Eye"
            iconPosition="left"
            iconSize={16}
          >
            عرض
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleEdit}
            iconName="Edit"
            iconPosition="left"
            iconSize={16}
          >
            تعديل
          </Button>
        </div>

        <Button
          variant={subUnion?.isActive ? "destructive" : "success"}
          size="sm"
          onClick={handleStatusToggle}
          iconName={subUnion?.isActive ? 'UserX' : 'UserCheck'}
          iconPosition="left"
          iconSize={16}
        >
          {subUnion?.isActive ? 'إلغاء التفعيل' : 'تفعيل'}
        </Button>
      </div>
    </div>
  );
};

export default SubUnionCard;